<template>
  <div>
    <confirm-action-dialog
      :isDialogActive.sync="isDialogActive"
      :title="dialogTitle"
      :subtitle="dialogSubtitle"
      :leftText="leftText"
      :rightText="rightText"
      :persistent="true"
      :supressKeyboardActions="true"
      :hideClose="step === 2"
      @close="close"
      @leftClick="onLeftClick"
      @rightClick="onRightClick"
    >
      <v-window v-model="step" touchless>
        <v-window-item :value="1">
          <div v-for="token of tokens" :key="token">
            <v-text-field
              :value="pendingTokenValues[token]"
              :label="getLabel(token)"
              @input="(v) => setPendingTokenValue(token, v)"
              outlined
              :append-icon="pendingTokenValues[token] ? 'mdi-content-copy' : undefined"
              @click:append="copyToClipboard(pendingTokenValues[token])"
            />
          </div>
        </v-window-item>

        <v-window-item :value="2"> </v-window-item>
      </v-window>
    </confirm-action-dialog>
  </div>
</template>

<script lang="ts">
import { getNestedObjectValues, setNestedObjectValues, toPlainObject } from "@/lib/objectPath-helper";
import { ITemplateExtendedContext } from "@/lib/interfaces/template/templateContext.interface";
import { Component } from "vue-property-decorator";
import Vue from "vue";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";

@Component({ components: { ConfirmActionDialog } })
export default class TemplateCardCustomTokenDialog extends Vue {
  isDialogActive = false;
  step = 1;

  tokens: string[] = [];
  pendingTokenValues: Record<string, string> = {};
  editableContext: ITemplateExtendedContext = {};
  userHasEditedBody = false;

  copyToClipboard(value: string) {
    navigator.clipboard.writeText(value);
    this.$toast.info(this.$t("components.partner.PartnerReportInitializeCard.linkCopied"));
  }

  getLabel(tokenPath: string) {
    const translationPrefix = "components.template.editor.placeholder.path";
    const translation = this.$t(`${translationPrefix}.${tokenPath}`).toString();
    if (translation.startsWith(translationPrefix)) {
      return tokenPath;
    }
    return translation;
  }

  get dialogTitle() {
    switch (this.step) {
      case 2:
        return this.$t("components.template.TemplateCard.overwriteTitle");
      default:
        return this.$t("components.template.dialog.preview.customTokens");
    }
  }

  get dialogSubtitle() {
    switch (this.step) {
      case 2:
        return this.$t("components.template.TemplateCard.overwriteSubtitle");
      default:
        return this.userHasEditedBody ? this.$t("components.template.TemplateCard.customTokensWarning") : undefined;
    }
  }

  get leftText() {
    switch (this.step) {
      case 2:
        return this.$t("back");
      default:
        return this.$t("close");
    }
  }

  get rightText() {
    switch (this.step) {
      case 2:
        return this.$t("yes");
      default:
        return this.userHasEditedBody ? this.$t("next") : this.$t("confirm");
    }
  }

  confirm() {
    if (this.userHasEditedBody) {
      this.step = 2;
      return;
    }
    this.apply(true);
  }

  onLeftClick() {
    switch (this.step) {
      case 2:
        this.step = 1;
        break;
      default:
        this.close();
    }
  }

  onRightClick() {
    switch (this.step) {
      case 2:
        this.apply(true);
        break;
      default:
        this.confirm();
    }
  }

  setPendingTokenValue(tokenPath: string, value: string) {
    this.pendingTokenValues = { ...this.pendingTokenValues, [tokenPath]: value };
    this.editableContext = setNestedObjectValues(toPlainObject(this.editableContext), tokenPath, value);
  }

  open(tokens: string[], extendedContext: ITemplateExtendedContext, userHasEditedBody = false) {
    this.isDialogActive = true;
    this.step = 1;
    this.tokens = tokens;
    this.userHasEditedBody = userHasEditedBody;

    try {
      this.editableContext = toPlainObject(extendedContext);

      const pendingTokenValues: Record<string, string> = {};
      for (const token of tokens) {
        const currentValue = getNestedObjectValues(this.editableContext, token);
        pendingTokenValues[token] = currentValue == null ? "" : String(currentValue);
      }
      this.pendingTokenValues = pendingTokenValues;
    } catch (e) {
      this.editableContext = {};
      this.pendingTokenValues = {};
    }
  }

  close() {
    this.isDialogActive = false;
    this.step = 1;
    this.tokens = [];
    this.pendingTokenValues = {};
    this.editableContext = {};
  }

  apply(regenerate: boolean) {
    this.$emit("apply", toPlainObject(this.editableContext), regenerate);
    this.close();
  }
}
</script>
